/**
 * Keeps link secrets out of telemetry.
 *
 * Some Atlas links carry a live credential in the URL itself: the password
 * reset link, the public evidence, design-intake and text links (and the API
 * routes their pages call), and the invitation link's `?token=`. GA, PostHog
 * and Sentry all record URLs — the page, the referrer, every fetch and
 * navigation — so each of them passes what it sends through here first: the
 * secret path segment becomes `[token]` and the value of a credential-named
 * query parameter becomes `[redacted]`.
 *
 * Works on a bare URL (absolute or relative) and on free text that quotes one
 * (a Sentry message, a span description). Pure, so the browser, server and
 * edge bundles all share it.
 */

/**
 * Routes whose next path segment is the credential. Mirrors the `[token]`
 * routes under src/app; a new public token route belongs here too.
 */
const TOKEN_ROUTE_PREFIXES = [
  '/reset-password/',
  // Evidence link (src/app/e) and its API.
  '/e/',
  '/api/evidence/',
  // Design-intake magic link (src/app/intake) and its API.
  '/intake/',
  '/api/intake/',
  // Tier 0 text link (src/app/r) and its API.
  '/r/',
  '/api/text-link/'
]

/**
 * A prefix counts only at the root of a path: at the start of the text, after
 * whitespace or a quote, or straight after an absolute URL's host — so
 * `/wci/internal/e/…` or a bundle path containing `/e/` is left alone.
 */
const TOKEN_SEGMENT = new RegExp(
  `(^|[\\s"'(<=,]|\\/\\/[^/\\s"'<>?#]+)(${TOKEN_ROUTE_PREFIXES.map((p) =>
    p.replace(/[/.]/g, '\\$&')
  ).join('|')})[^/?#\\s"'<>]+`,
  'g'
)

/**
 * Query or fragment parameter names whose value is a credential: exact `key`,
 * `sig`…, or any name containing token (but not a count: `tokens`,
 * `token_count`), secret, password, signature, credential or api-key
 * (`access_token`, `X-Amz-Signature`, `apiKey`).
 *
 * Not `code`: Atlas pages carry the technician code as `?code=`, which is no
 * secret, and the one secret `code` — the Microsoft OAuth callback's — is
 * single-use and PKCE-bound, so worthless without the verifier the server
 * keeps.
 */
const SECRET_PARAM_NAME =
  /^(?:key|sig|grant|otp|ticket|samlrequest|samlresponse)$|token(?!s(?:$|[._-])|_?count)|secret|passw(?:or)?d|signature|credential|api[-_]?key/i

/** `name=value` after `?`, `&`, `;`, `#`, or at the start (a bare query). */
const PARAM = /(^|[?&;#])([^=&#?\s"'<>]+)=([^&#\s"'<>]*)/g

const redactParam = (
  match: string,
  sep: string,
  name: string,
  value: string
) =>
  value && SECRET_PARAM_NAME.test(name) ? `${sep}${name}=[redacted]` : match

/** `text` with every link secret in it redacted. */
export function redactUrl(text: string): string {
  if (!text) return text
  return text.replace(TOKEN_SEGMENT, '$1$2[token]').replace(PARAM, redactParam)
}

/** Whether `url` carries a link secret that `redactUrl` would remove. */
export function hasLinkSecret(url: string): boolean {
  return redactUrl(url) !== url
}

const MAX_DEPTH = 6

/**
 * Redacts every string inside `value` in place (objects and arrays, to a
 * bounded depth, cycle-safe) and returns it — for structured payloads such as
 * a Sentry event or a PostHog event's properties, whose URLs sit under keys
 * that vary by SDK version.
 */
export function redactUrlsIn<T>(value: T): T {
  if (typeof value === 'string') return redactUrl(value) as T
  walk(value, 0, new WeakSet())
  return value
}

function walk(value: unknown, depth: number, seen: WeakSet<object>): void {
  if (value === null || typeof value !== 'object' || depth >= MAX_DEPTH) return
  if (seen.has(value)) return
  seen.add(value)
  const node = value as Record<string, unknown>
  for (const key of Object.keys(node)) {
    const item = node[key]
    if (typeof item === 'string') {
      const redacted = redactUrl(item)
      if (redacted !== item) node[key] = redacted
    } else {
      walk(item, depth + 1, seen)
    }
  }
}
